<template>
    <div class="programs">
        <div
            v-for="(items, i) in rows"
            class="row"
            :key="i"
        >
            <div
                v-for="item in items"
                class="item"
                :class="[item.class, { active: item.programId === selectedProgramId }]"
                :style="item.style"
                :key="item.id"
                @click="onClickBlock(item)"
            >
                <div class="icon-holder"></div>
                <div class="name-holder">{{ getName(item) }}</div>
            </div>
        </div>
        <div class="spacer">&nbsp;</div>
    </div>
</template>

<script>
import moment from 'moment';
import {mapGetters} from 'vuex';

const getters = [
    'getTrackStatus',
    'getTimelinePrograms',
];

function isIntersect(a, b) {
    let aStart = a.offset;
    let aEnd = a.offset + a.size;
    let bStart = b.offset;
    let bEnd = b.offset + b.size;
    return aEnd > bStart && bEnd > aStart;
}

export default {
    name: 'TimelineProgramsDesktop',
    props: {
        monthsToShow: {
            type: Number,
            required: true,
        },
        monthsOffset: {
            type: Number,
            required: true,
        },
        nowOffset: {
            type: Number,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        selectedProgramId: {
            type: [String, Number],
            default: null,
        },
    },
    data: () => ({
        programColors: {
            higher: '94,193,249',
            additional: '175,119,225',
            secondary: '234, 165, 32',
            webinar: '76,224,189',
            course: '242,126,130',
        },
        showTextPercentage: .75,
    }),
    computed: {
        ...mapGetters('accountTrack', getters),

        rows() {
            return this.makeRows(this.sortItems(this.items));
        },
    },
    methods: {
        getName(item) {
            return [item.programName, item.moduleName]
                .filter(v => v)
                .join('. ')
            ;
        },
        isOnScreen(v) {
            return v.offset + v.size >= this.monthsOffset && v.offset < this.monthsOffset + this.monthsToShow;
        },
        onClickBlock(item) {
            this.$emit('select', item);
        },
        makeRows(items) {
            return items
                .filter(this.isOnScreen)
                .reduce((rows, item) => {
                    let blocks = [];

                    let programFields = {
                        programId: item.id,
                        programName: item.name,
                        programType: item.programType,
                        moduleId: null,
                        moduleName: null,
                        moment: {
                            start: item.moment.start.clone(),
                            end: item.moment.end.clone(),
                            duration: item.moment.duration.clone(),
                        },
                        hasUpdates: item.hasUpdates,
                        status: item.status,
                        size: item.size,
                        offset: item.offset,
                        class:  null,
                        style:  null,
                    };

                    if (item.modules.length === 0) {
                        programFields.class = this.getProgramItemClass(item);
                        programFields.style = this.getProgramItemStyle(item);
                    } else {
                        blocks = item.modules.map(module => {
                            let v = {
                                ...programFields,
                                moduleId: module.id,
                                moduleName: module.name,
                                hasUpdates: module.hasUpdates,
                                status: module.status,
                                size: module.size,
                                offset: module.offset,
                            };
                            v.class = this.getProgramItemClass(v);
                            v.style = this.getProgramItemStyle(v);
                            return v;
                        }).filter(this.isOnScreen);
                    }

                    let ri = 0;
                    while (ri < rows.length) {
                        if (!rows[ri].some(el => isIntersect(el, item))) {
                            break;
                        }
                        ri++;
                    }

                    if (ri === rows.length) {
                        rows.push([]);
                    }

                    if (blocks.length === 0) {
                        blocks.push(programFields);
                    }

                    rows[ri] = [
                        ...rows[ri],
                        ...blocks,
                    ];

                    return rows;
                }, []);
        },
        sortItems(items) {
            return items.sort((a, b) => {
                if (a.size === b.size) {
                    return a.offset < b.offset ? 1 : -1;
                } else {
                    return a.size <= b.size ? 1 : -1;
                }
            });
        },
        getProgramItemStyle(v) {
            let coef = 100 / this.monthsToShow;
            let left = v.offset - this.monthsOffset;
            let fill = v.moment.end.isAfter(moment())
                ? (this.nowOffset - v.offset) / v.size * 100
                : 100;

            let styles = {
                left: `${left * coef}%`,
                width: `${v.size * coef}%`,
                background: `linear-gradient(
                    to right,
                    rgba(${this.programColors[v.programType]},0.5) 0%,
                    rgba(${this.programColors[v.programType]},0.5) ${fill}%,
                    rgba(${this.programColors[v.programType]},0.25) ${fill}%,
                    rgba(${this.programColors[v.programType]},0.25) 100%
                )`,
            };

            if (this.monthsOffset > v.offset) {
                styles.paddingLeft = `${(this.monthsOffset - v.offset + 0.05) * coef}%`;
            }
            return styles;
        },
        getProgramItemClass(v) {
            let extraSmall = this.showTextPercentage > v.size + v.offset - this.monthsOffset;
            return {
                [`type-${v.programType}`]: true,
                'large': this.showTextPercentage < v.size && !extraSmall,
                'marker': v.hasUpdates,
                'small-remaining': extraSmall,
                'completed': v.status === 'completed',
            };
        },
    },
};
</script>

<style lang="scss" scoped>
    @import '~@/styles/rem';

    .programs {
        display: flex;
        flex-direction: column-reverse;
        min-height: 432px;
        .spacer {
            min-height: 32px;
            min-width: 100%;
        }

        .row {
            width: 100%;
            min-height: 56px;
            overflow-x: hidden;
            padding-top: 8px;
            position: relative;

            .item {
                position: absolute;
                background: #5EC1F9;
                border-radius: 4px;
                padding-right: 0;
                height: 48px;
                font-size: 8px;
                cursor: pointer;

                &.small-remaining {
                    padding-right: 12px;
                    padding-left: 12px;
                }
            }
        }
    }

    .marker::before {
        position: absolute;
        top: -5px;
        content: " ";
        background: #D1004A;
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }


    $types: (
        "higher": #1A73A6,
        "additional": #573FC5,
        "secondary": #BC7D04,
        "webinar": #0D9A8C,
        "course": #DC687C
    );

    $borders: (
        "higher": #5EC1F9,
        "additional": #AF77E1,
        "secondary": #EAA520,
        "webinar": #4ACFC2,
        "course": #F27E82
    );

    .name-holder {
        display: none;
    }
    @each $type, $color in $types {
        .type-#{$type} {
            display: flex;
            justify-content: center;
            align-items: center;

            &.large {
                justify-content: flex-start;
                padding: 0 9px;

                .name-holder {
                    padding: 0 10px;
                    display: block;
                    font-size: 12px;
                    line-height: 16px;
                    color: $color;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-height: 32px;
                }
            }

            .icon-holder {
                display: block;
                min-width: 24px;
                min-height: 24px;
                background-image: url("../../../../assets/images/account-track/icon/program/#{$type}.svg");
                background-repeat: no-repeat;
                background-size: cover;
            }

            &.completed .icon-holder{
                background-image: url("../../../../assets/images/account-track/icon/check/#{$type}.svg");
            }

        }
    }

    @each $type, $color in $borders {
        .type-#{$type} {
            border: 2px solid transparent;

            &:hover {
                border: 2px solid rgba($color, 0.25);
            }

            &.active {
                border: 2px solid $color;
            }
        }
    }

</style>
